import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Heading,
    Flex,
    Box,
    SimpleGrid,
    Button,
    Divider,
    Spacer,
    Accordion,
    AccordionItem,
    AccordionPanel,
    Text,
    useDisclosure,
    useMediaQuery,
    MenuItem,
} from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react/macro';
import { Formik, Form } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { loadModules } from 'esri-loader';

import {
    createSubBattery,
    deletebattery,
    deleteSubBattery,
    editBatteryAndSubbatteries,
    getBatteryDefinitions,
    getBiddingStrategyTemplates,
} from '../../services/batteries';
import { checkItemInputEvent, flatItemDescriptions } from '../../services/items';

import { checkCoordsDigits } from '../../services/portfolios';

import useCommonToast from '../../hooks/useCommonToast';
import useHistoryPush from '../../hooks/useHistoryPush';

import ExpandableItemWrapper from '../layout/ExpandableItemWrapper';
import ShowHideSidebarButton from '../layout/ShowHideSidebarButton';

import Tooltip from '../utils/Tooltip';
import Error from '../utils/Error';
import DefinitionsSkeleton from '../utils/DefinitionsSkeleton';
import AddSubItem from '../utils/AddSubItem';
import ItemPropertiesModal from '../utils/ItemPropertiesModal';
import DeleteRecordModal from '../utils/DeleteRecordModal';
import SubItemActionsMenu from '../../components/itemData/SubItemActionsMenu';
import ConfirmationModal from '../utils/ConfirmationModal';
import CircularIconButton from '../utils/CircularIconButton';
import AccordionIconButton from '../utils/AccordionIconButton';
import SecondaryIconButton from '../utils/SecondaryIconButton';

import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';

import { ReactComponent as BatteryIcon } from '../../icons/item-icons/battery.svg';
import { ReactComponent as EditIcon } from '../../icons/edit-circled.svg';
import { ReactComponent as FileIcon } from '../../icons/file.svg';
import { ReactComponent as DeleteIcon } from '../../icons/delete-circled.svg';
import { ReactComponent as DeleteBatteryIcon } from '../../icons/delete.svg';
import { ReactComponent as SaveIcon } from '../../icons/save.svg';
import { ReactComponent as ChevronDownIcon } from '../../icons/chevron-down.svg';
import { ReactComponent as AddItemIcon } from '../../icons/item-icons/add-item.svg';

import BatteryDefinitions from './BatteryDefinitions';
import AddSubBatteryModal from './AddSubBatteryModal';
import SubBatteryData from './SubBatteryData';

const Battery = ({
    batteryItem,
    typeId,
    onBatteryEdit,
    onBatteryDelete,
    onNodeRename,
    onNodeOwnershipPercentageUpdate,
    onNodeItemDelete,
    refreshMapLayer,
}) => {
    const intl = useIntl();
    const toast = useCommonToast();
    const { batteryId } = useParams();
    const [isDesktop] = useMediaQuery('(min-width: 769px)');
    const historyPush = useHistoryPush();

    const deleteModal = useDisclosure();
    const propertiesModal = useDisclosure();
    const addSubBatteryModal = useDisclosure();
    const deleteBatteryModal = useDisclosure();

    const [selectedBattery, setSelectedBattery] = useState(null);
    const [batteryDefinitions, setBatteryDefinitions] = useState(null);
    const [loadingError, setLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [editDescriptionsConfig, setEditDescriptionsConfig] = useState(null);
    const [templatesConfig, setTemplatesConfig] = useState(null);
    const [subBatteryForDeletion, setSubBatteryForDeletion] = useState(null);

    const selectedBatteryRef = useRef(null);

    const mapBatteryDefinitionsConfig = useCallback(
        (definitions) => {
            return definitions.map((def) => {
                const config = {};

                config['batteryId'] = def.id; //BE expects sub battery id as 'batteryId'
                config['itemId'] = batteryId ? parseInt(batteryId) : parseInt(batteryItem.properties.id);
                config['description'] = def.description;
                config['batteryTypeId'] = def.batteryTypeId || undefined;

                return config;
            });
        },
        [batteryId, batteryItem?.properties.id]
    );

    const mapBiddingStrategyTemplates = (subBatteries) => {
        const config = {};

        subBatteries.forEach((def) => {
            config[`${def.id}`] = { templateId: def.templateId, hasUpload: false };
        });

        return config;
    };

    const initialFormValues = {
        battery: {
            itemId: selectedBattery?.id,
            description: selectedBattery?.description,
            bookId: selectedBattery?.bookId,
            pricingPointId: selectedBattery?.pricingPointId || undefined,
            itemTypeId: typeId || batteryItem.properties.typeId,
            latitude: selectedBattery?.latitude !== null ? selectedBattery?.latitude : '',
            longitude: selectedBattery?.longitude !== null ? selectedBattery?.longitude : '',
        },
        subBatteries: batteryDefinitions?.initialFormValues,
    };

    const fetchSubBatteriesData = useCallback(() => {
        setLoadingError(false);

        Promise.all([getBatteryDefinitions(batteryId || batteryItem.properties.id), getBiddingStrategyTemplates()])
            .then(([batteryItemData, batteryBiddingStrategyTemplates]) => {
                setSelectedBattery(batteryItemData.batteryItem);
                selectedBatteryRef.current = batteryItemData.batteryItem;

                const defaultTemplate = {
                    id: null,
                    description: intl.formatMessage({ id: 'common_custom' }),
                };
                setBatteryDefinitions({
                    definitions: {
                        ...batteryItemData,
                        templates: [defaultTemplate, ...batteryBiddingStrategyTemplates.data],
                    },
                    initialFormValues: mapBatteryDefinitionsConfig(batteryItemData.subBatteries),
                });

                setEditDescriptionsConfig(
                    flatItemDescriptions(batteryItemData.subBatteries, batteryId || batteryItem.properties.id)
                );

                setTemplatesConfig(mapBiddingStrategyTemplates(batteryItemData.subBatteries));
            })
            .catch(() => setLoadingError(true))
            .finally(() => setIsLoading(false));
    }, [batteryId, batteryItem?.properties.id, intl, mapBatteryDefinitionsConfig]);

    useEffect(() => {
        setIsLoading(true);
        fetchSubBatteriesData();
    }, [fetchSubBatteriesData]);

    const formLabels = {
        book: intl.formatMessage({ id: 'batteries_book' }),
        pricingPoint: intl.formatMessage({ id: 'batteries_pricing_point' }),
        selectPlaceholder: intl.formatMessage({ id: 'common_forms_select_option' }),
        batteryType: intl.formatMessage({ id: 'batteries_battery_type' }),
        latitude: intl.formatMessage({ id: 'latitude' }),
        longitude: intl.formatMessage({ id: 'longitude' }),
    };

    const onBatteryFormSubmit = async (values, actions) => {
        const successMessage = intl.formatMessage({ id: 'batteries_save_changes_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            const {
                battery: { itemId, description, bookId, pricingPointId, itemTypeId, latitude, longitude },
                subBatteries,
            } = values;
            const ppId = pricingPointId || null;

            const [webMercatorUtils] = await loadModules(['esri/geometry/support/webMercatorUtils']);
            const [x, y] = webMercatorUtils.lngLatToXY(values.battery.longitude, values.battery.latitude);

            await editBatteryAndSubbatteries({
                battery: {
                    description,
                    bookId,
                    itemTypeId,
                    pricingPointId: ppId,
                    itemId,
                    longitude: longitude !== '' ? longitude : null,
                    latitude: latitude !== '' ? latitude : null,
                    x: longitude !== '' ? x : null,
                    y: latitude !== '' ? y : null,
                },
                subBatteries,
            });

            fetchSubBatteriesData();

            if (!batteryItem) {
                onBatteryEdit({
                    id: itemId,
                    description,
                    bookId,
                    pricingPointId,
                    securityId: selectedBattery.securityId,
                });
            } else {
                onNodeRename(description);
            }

            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }

        if (typeof refreshMapLayer === 'function') {
            refreshMapLayer({
                long: values.battery.longitude,
                lat: values.battery.latitude,
                objectRefId: selectedBatteryRef.current.objectRefId,
            });
        }
        actions.setSubmitting(false);
    };

    const onEditDescription = (batteryId) => {
        if (!editDescriptionsConfig[batteryId].penDisabled) {
            setEditDescriptionsConfig((prevState) => {
                return {
                    ...prevState,
                    [batteryId]: { isActive: !prevState[batteryId].isActive, penDisabled: false },
                };
            });
        }
    };

    const validateDescription = (value, which, batteryId) => {
        const trimmed = value.trim();
        const maxLength = which === 'battery' ? 100 : 50;
        const label = which.charAt(0).toUpperCase() + which.slice(1);
        let errorMessage = '';

        if (trimmed.length === 0) {
            errorMessage = intl.formatMessage({ id: 'common_forms_validation_required' }, { label: `${label} name` });
        } else if (trimmed.length > maxLength) {
            errorMessage = intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: `${label} name`, lengthRule: `${maxLength} characters or less` }
            );
        }

        if (errorMessage !== '') {
            setEditDescriptionsConfig((prevState) => {
                return { ...prevState, [batteryId]: { ...prevState[batteryId], penDisabled: true } };
            });
        } else {
            setEditDescriptionsConfig((prevState) => {
                return { ...prevState, [batteryId]: { ...prevState[batteryId], penDisabled: false } };
            });
        }

        return errorMessage;
    };

    const validateBtn = (errors) => {
        if (errors?.battery || errors?.subBatteries) {
            return true;
        }
        return false;
    };

    const validate = (values) => {
        let longitude = validateLongLat(values.battery.longitude, 'long', values.battery.latitude);
        let latitude = validateLongLat(values.battery.latitude, 'lat', values.battery.longitude);

        if (!longitude && !latitude) {
            return null;
        }

        return {
            battery: {
                longitude: longitude ? longitude : null,
                latitude: latitude ? latitude : null,
            },
        };
    };

    const validateLongLat = (val, type, sibling) => {
        let errorMessage;

        if (type === 'long') {
            if (val >= 180 || val <= -180) {
                errorMessage = intl.formatMessage({ id: 'longitude_validation_borders' });
            }
        }
        if (type === 'lat') {
            if (val > 85 || val < -85) {
                errorMessage = intl.formatMessage({ id: 'latitude_validation_borders' });
            }
        }

        if (val !== '' && sibling === '') {
            errorMessage = intl.formatMessage({ id: 'longlat_validation_both' });
        }

        let digitsError = checkCoordsDigits(val);
        if (digitsError) {
            errorMessage = intl.formatMessage({ id: 'longlat_validation_decimal' });
        }

        return errorMessage;
    };

    const onDeleteModalOpen = (subBatteryId, subBatteryDesc) => {
        setSubBatteryForDeletion({
            id: subBatteryId,
            description: subBatteryDesc,
        });
        deleteModal.onOpen();
    };

    const onDeleteModalClose = () => {
        deleteModal.onClose();
        setSubBatteryForDeletion(null);
    };

    const onSubBatteryDelete = async (subBatteryId) => {
        onDeleteModalClose();

        const successMessage = intl.formatMessage({ id: 'common_delete_success' }, { item: 'installation' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            setIsLoading(true);

            await deleteSubBattery(subBatteryId);
            fetchSubBatteriesData();

            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const updateBatteries = ({ id, batteryTypeId, description }, itemId) => {
        const typeId = batteryTypeId || undefined;

        initialFormValues.subBatteries.push({ batteryId: id, batteryTypeId: typeId, description, itemId });

        setTemplatesConfig((prev) => {
            return {
                ...prev,
                [id]: { templateId: null, hasUpload: false },
            };
        });

        setEditDescriptionsConfig((prev) => {
            return {
                ...prev,
                [id]: { isActive: false, penDisabled: false },
            };
        });
    };

    const onItemDelete = async (item) => {
        const successMessage = intl.formatMessage({ id: 'common_delete_success' }, { item: 'battery item' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            await deletebattery(item.id);

            if (batteryItem) {
                onNodeItemDelete();
            } else {
                onBatteryDelete(item);
                historyPush('/batteries');
            }

            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    const onTemplatesConfigChange = (subBatteryId, config) => {
        setTemplatesConfig((prevState) => {
            return {
                ...prevState,
                [subBatteryId]: config,
            };
        });
    };

    return (
        <ExpandableItemWrapper>
            {isLoading ? (
                <DefinitionsSkeleton />
            ) : loadingError ? (
                <Box m={6}>
                    <Error primaryId="common_error" secondaryId="common_loading_error" additionalText="definitions" />
                </Box>
            ) : (
                <>
                    <Formik
                        enableReinitialize
                        initialValues={initialFormValues}
                        onSubmit={onBatteryFormSubmit}
                        validate={validate}
                    >
                        {({ isSubmitting, dirty, errors, values }) => (
                            <Form>
                                <Flex pt={3} pl={6} align="center">
                                    <CustomBatteryIcon isMaster={selectedBattery.securityId} />

                                    {!editDescriptionsConfig[`${selectedBattery.id}`]?.isActive ? (
                                        <Heading px={2} as="h2" variant="h2">
                                            {values.battery.description}
                                        </Heading>
                                    ) : (
                                        <InputFormikField
                                            px={3}
                                            name="battery.description"
                                            type="text"
                                            flex="1"
                                            maxW="500px"
                                            validate={(value) =>
                                                validateDescription(value, 'battery', selectedBattery.id)
                                            }
                                            onKeyDown={checkItemInputEvent}
                                            isFastField
                                        />
                                    )}

                                    <Box display="inline-block" mr={4}>
                                        <Tooltip
                                            label={intl.formatMessage({ id: 'common_edit_btn_tooltip_and_label' })}
                                        >
                                            <CircularIconButton
                                                aria-label={intl.formatMessage({
                                                    id: 'common_edit_btn_tooltip_and_label',
                                                })}
                                                leftIcon={<EditIcon />}
                                                onClick={() => onEditDescription(selectedBattery.id)}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Flex>

                                <ActionBar>
                                    <ShowHideSidebarButton />

                                    <SecondaryIconButton
                                        leftIcon={<FileIcon />}
                                        size="sm"
                                        variant="solid"
                                        flexShrink={0}
                                        onClick={propertiesModal.onOpen}
                                    >
                                        <FormattedMessage id="portfolio_item_properties" />
                                    </SecondaryIconButton>

                                    <Spacer display={{ base: 'none', xl: 'block' }} />

                                    <Button
                                        leftIcon={<DeleteBatteryIcon />}
                                        type="button"
                                        variant="special"
                                        size="sm"
                                        onClick={deleteBatteryModal.onOpen}
                                    >
                                        <FormattedMessage id="common_delete_item_btn" />
                                    </Button>

                                    <Button
                                        leftIcon={<SaveIcon />}
                                        isDisabled={dirty ? validateBtn(errors) : true}
                                        isLoading={isSubmitting}
                                        type="submit"
                                        size="sm"
                                    >
                                        <FormattedMessage id="common_save_changes_button" />
                                    </Button>
                                </ActionBar>

                                <Divider />

                                <SimpleGrid pt={7} pb={5} px={6} columns={{ xl: 4 }} spacingX={6} spacingY={4}>
                                    <ComboboxFormikField
                                        id="battery.bookId"
                                        name="battery.bookId"
                                        label={formLabels.book}
                                        options={batteryDefinitions.definitions.books}
                                        valueKey="id"
                                        labelKey="name"
                                        isFastField
                                    />

                                    <ComboboxFormikField
                                        id="battery.pricingPointId"
                                        name="battery.pricingPointId"
                                        label={formLabels.pricingPoint}
                                        info={<FormattedMessage id="common_pricing_point_empty_tooltip" />}
                                        options={batteryDefinitions.definitions.pricingPoints}
                                        labelKey="description"
                                        valueKey="id"
                                        showPlaceholder={true}
                                        isFastField
                                    />

                                    <InputFormikField
                                        id="battery.latitude"
                                        name="battery.latitude"
                                        label={formLabels.latitude}
                                        type="number"
                                        isFastField
                                    />

                                    <InputFormikField
                                        id="battery.longitude"
                                        name="battery.longitude"
                                        label={formLabels.longitude}
                                        type="number"
                                        isFastField
                                    />
                                </SimpleGrid>

                                <Box>
                                    {initialFormValues.subBatteries.length === 0 ? (
                                        <AddSubItem onOpen={addSubBatteryModal.onOpen} />
                                    ) : (
                                        <CustomAccordion allowToggle defaultIndex={[0]}>
                                            {initialFormValues.subBatteries.map((def, idx) => {
                                                const foundEditDescriptionsConfig =
                                                    editDescriptionsConfig[`${def.batteryId}`];
                                                const isEditing = foundEditDescriptionsConfig
                                                    ? foundEditDescriptionsConfig.isActive
                                                    : false;

                                                const templateConfig = templatesConfig[def.batteryId];
                                                const templateId = templateConfig && templateConfig.templateId;

                                                return (
                                                    <AccordionItem key={def.batteryId} border="none">
                                                        {({ isExpanded }) => (
                                                            <>
                                                                <CustomSimpleGrid columns={2} isExpanded={isExpanded}>
                                                                    <Flex>
                                                                        <AccordionIconButton isEditing={isEditing}>
                                                                            {isExpanded ? (
                                                                                <CustomChevronUpIcon />
                                                                            ) : (
                                                                                <CustomChevronDownIcon />
                                                                            )}
                                                                        </AccordionIconButton>

                                                                        <Box my={3}>
                                                                            <Text size="xs" mb="2px">
                                                                                <FormattedMessage id="batteries_sub_battery_heading" />
                                                                            </Text>

                                                                            {!isEditing ? (
                                                                                <Heading as="h4" variant="h4">
                                                                                    {
                                                                                        values.subBatteries[idx]
                                                                                            ?.description
                                                                                    }
                                                                                </Heading>
                                                                            ) : (
                                                                                <InputFormikField
                                                                                    id={'description' + idx}
                                                                                    name={`subBatteries[${idx}].description`}
                                                                                    type="text"
                                                                                    validate={(value) =>
                                                                                        validateDescription(
                                                                                            value,
                                                                                            'sub-battery',
                                                                                            def.batteryId
                                                                                        )
                                                                                    }
                                                                                    onKeyDown={checkItemInputEvent}
                                                                                    isFastField
                                                                                />
                                                                            )}
                                                                        </Box>

                                                                        <Flex align="center" ml={3}>
                                                                            <Tooltip
                                                                                label={intl.formatMessage({
                                                                                    id: 'common_edit_btn_tooltip_and_label',
                                                                                })}
                                                                            >
                                                                                <CircularIconButton
                                                                                    aria-label={intl.formatMessage({
                                                                                        id: 'common_edit_btn_tooltip_and_label',
                                                                                    })}
                                                                                    leftIcon={<EditIcon />}
                                                                                    isEditing={isEditing}
                                                                                    onClick={() =>
                                                                                        onEditDescription(def.batteryId)
                                                                                    }
                                                                                />
                                                                            </Tooltip>
                                                                        </Flex>
                                                                    </Flex>

                                                                    <Flex
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        justify="flex-end"
                                                                        pr={6}
                                                                    >
                                                                        {!isDesktop ? (
                                                                            <SubItemActionsMenu
                                                                                menuListItems={
                                                                                    <>
                                                                                        <MenuItem
                                                                                            key="delete-btn"
                                                                                            textTransform="capitalize"
                                                                                            onClick={() => {
                                                                                                onDeleteModalOpen(
                                                                                                    def.batteryId,
                                                                                                    def.description
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            <FormattedMessage id="common_delete" />
                                                                                        </MenuItem>

                                                                                        <MenuItem
                                                                                            key="create-btn"
                                                                                            textTransform="capitalize"
                                                                                            onClick={
                                                                                                addSubBatteryModal.onOpen
                                                                                            }
                                                                                        >
                                                                                            <FormattedMessage id="common_add" />
                                                                                        </MenuItem>
                                                                                    </>
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <>
                                                                                <Tooltip
                                                                                    label={intl.formatMessage({
                                                                                        id: 'common_delete_btn_tooltip_and_label',
                                                                                    })}
                                                                                >
                                                                                    <CustomDeleteIcon
                                                                                        onClick={() => {
                                                                                            onDeleteModalOpen(
                                                                                                def.batteryId,
                                                                                                def.description
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                </Tooltip>

                                                                                <Divider
                                                                                    orientation="vertical"
                                                                                    ml={5}
                                                                                    mr={5}
                                                                                />

                                                                                <Tooltip
                                                                                    label={intl.formatMessage({
                                                                                        id: 'common_add_new',
                                                                                    })}
                                                                                >
                                                                                    <CustomAddItemIcon
                                                                                        onClick={
                                                                                            addSubBatteryModal.onOpen
                                                                                        }
                                                                                    />
                                                                                </Tooltip>
                                                                            </>
                                                                        )}
                                                                    </Flex>
                                                                </CustomSimpleGrid>

                                                                {isExpanded && (
                                                                    <AccordionPanel px={6} pt={6} pb={4}>
                                                                        <BatteryDefinitions
                                                                            idx={idx}
                                                                            formLabels={formLabels}
                                                                            definitions={batteryDefinitions.definitions}
                                                                        />

                                                                        <SubBatteryData
                                                                            subBatteryId={def.batteryId}
                                                                            expanded={isExpanded}
                                                                            ancillaries={batteryDefinitions.definitions.ancillaries.sort(
                                                                                (a, b) =>
                                                                                    a.description.localeCompare(
                                                                                        b.description
                                                                                    )
                                                                            )}
                                                                            peakPeriods={
                                                                                batteryDefinitions.definitions
                                                                                    .peakPeriods
                                                                            }
                                                                            quantityTypes={
                                                                                batteryDefinitions.definitions
                                                                                    .quantityTypes
                                                                            }
                                                                            templates={
                                                                                batteryDefinitions.definitions.templates
                                                                            }
                                                                            templateId={templateId}
                                                                            templateConfig={
                                                                                templateConfig || {
                                                                                    templateId: null,
                                                                                    hasUpload: false,
                                                                                }
                                                                            }
                                                                            onTemplatesConfigChange={
                                                                                onTemplatesConfigChange
                                                                            }
                                                                        />
                                                                    </AccordionPanel>
                                                                )}
                                                            </>
                                                        )}
                                                    </AccordionItem>
                                                );
                                            })}
                                        </CustomAccordion>
                                    )}
                                </Box>
                            </Form>
                        )}
                    </Formik>

                    {deleteModal.isOpen && (
                        <DeleteRecordModal
                            onClose={onDeleteModalClose}
                            headerItem="Installation"
                            contentMessageItem={subBatteryForDeletion.description}
                            onConfirm={() => {
                                onSubBatteryDelete(subBatteryForDeletion.id);
                            }}
                        />
                    )}

                    {propertiesModal.isOpen && (
                        <ItemPropertiesModal
                            isOpen
                            onClose={propertiesModal.onClose}
                            itemId={selectedBattery.id}
                            portfolio={batteryItem}
                            type="battery"
                            onNodeOwnershipPercentageUpdate={onNodeOwnershipPercentageUpdate}
                        />
                    )}

                    {addSubBatteryModal.isOpen && (
                        <AddSubBatteryModal
                            isOpen
                            headerItem="Installation"
                            batteryTypes={batteryDefinitions.definitions.batteryTypes}
                            batteryItemId={batteryId || batteryItem.properties.id}
                            updateBatteries={updateBatteries}
                            onSubmit={createSubBattery}
                            onClose={addSubBatteryModal.onClose}
                        />
                    )}

                    {deleteBatteryModal.isOpen && (
                        <ConfirmationModal
                            isOpen
                            onClose={deleteBatteryModal.onClose}
                            header={<FormattedMessage id="common_delete_modal_heading" values={{ item: 'Item' }} />}
                            hasExtraStep
                            content={
                                <FormattedMessage
                                    id="common_delete_modal_msg"
                                    values={{ item: selectedBattery.description }}
                                />
                            }
                            confirmText={
                                <Box as="span" textTransform="capitalize">
                                    <FormattedMessage id="common_delete" />
                                </Box>
                            }
                            onConfirm={() => {
                                deleteBatteryModal.onClose();
                                onItemDelete(selectedBattery);
                            }}
                        />
                    )}
                </>
            )}
        </ExpandableItemWrapper>
    );
};

const CustomBatteryIcon = styled(BatteryIcon, { shouldForwardProp: (prop) => prop !== 'isMaster' })`
    height: 32px;
    width: 32px;
    color: ${(props) => (props.isMaster ? 'var(--chakra-colors-green-500)' : 'var(--chakra-colors-gray-600)')};
    flex-shrink: 0;
`;

const ActionBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    width: 100%;
    padding: 12px 24px;
    gap: 16px;

    button {
        text-transform: capitalize;
        width: 100%;
    }

    button:first-of-type {
        margin-top: 4px;
    }

    @media (min-width: 769px) {
        flex-direction: row;

        button:first-of-type {
            margin-top: 0px;
        }
    }

    @media (min-width: 1280px) {
        button {
            width: auto;
        }
    }
`;

const ActionItemStyle = css`
    circle {
        fill: var(--chakra-colors-gray-100);
    }

    path {
        fill: var(--chakra-colors-gray-600);
    }

    &:hover {
        cursor: pointer;
        path {
            fill: var(--chakra-colors-blue-400);
        }
    }
`;

const CustomChevronDownIcon = styled(ChevronDownIcon)`
    ${ActionItemStyle}
`;

const CustomChevronUpIcon = styled(CustomChevronDownIcon)`
    transform: rotate(180deg);
`;

const CustomDeleteIcon = styled(DeleteIcon)`
    ${ActionItemStyle}
`;

const CustomAddItemIcon = styled(AddItemIcon)`
    ${ActionItemStyle}
`;

const CustomSimpleGrid = styled(SimpleGrid, { shouldForwardProp: (prop) => prop !== 'isExpanded' })`
    background: var(--chakra-colors-gray-50);
    border: var(--chakra-borders-1px);
    border-color: var(--chakra-colors-gray-200);
    border-left: var(--chakra-borders-none);
    border-bottom: ${(props) => !props.isExpanded && '1px'};
`;

const CustomAccordion = styled(Accordion)`
    .chakra-accordion__item:last-child {
        border-bottom: 1px solid var(--chakra-colors-gray-200);
    }
`;

export default Battery;
